import Card from "@/atoms/account/Card";
import { CardTitle } from "../typography";
import Divider from "@/atoms/account/Divider";
import EditBilling from "../EditBilling";
import { memo, useContext, useEffect, useState } from "react";
import { AccountContext } from "contexts/AccountProvider";
import DefaultBilling from "../DefaultBilling";
import AccountModal from "../AccountModal";
import { SubscriptionsContext } from "contexts/SubscriptionsProvider";
import Highlight from "@/atoms/account/Highlight";
import Button from "@/atoms/Button";

const BillingModal = memo(
  ({ isOpen, onClose, onToken, errorMsg, modalTitle, buttonLabel }) => {
    return (
      <AccountModal isOpen={isOpen} onClose={onClose} $maxWidth={"800px"}>
        <div className={"modal-title mb-10"}>{modalTitle}</div>
        <EditBilling
          onToken={onToken}
          errorMsg={errorMsg}
          buttonLabel={buttonLabel}
        />
      </AccountModal>
    );
  }
);

const Billing = ({
  defaultPaymentMethod,
  addPaymentMethod,
  hasNonGiftSub,
  outstandingBalance,
  availableCredit,
}) => {
  const [edit, setEdit] = useState(false);
  const [error, setError] = useState();

  const handleSubmit = ({ paymentToken, paymentType }) => {
    const onSuccess = () => {
      setEdit(false);
      setError(null);
    };
    return addPaymentMethod({
      paymentType,
      paymentToken,
      onSuccess,
      toastOnError: false,
      successMessage: "Successfully added payment method.",
      onError: (err) => setError(err.message),
    });
  };

  useEffect(() => {
    setError(null);
  }, [edit]);

  return (
    <Card className={"relative"}>
      <div id={"billing-a"} className={"absolute -mt-12"} />
      <CardTitle>Billing</CardTitle>
      <Divider className={"my-4"} />
      {outstandingBalance && (
        <Highlight error className={"mb-4"}>
          <div className={"text-error flex justify-between"}>
            <span>Balance due</span>
            <span className={"font-bold"}>{outstandingBalance}</span>
          </div>
        </Highlight>
      )}
      {availableCredit && (
        <Highlight className={"mb-4"}>
          <div className={"text-perriwinkle flex justify-between"}>
            <span>Available credit</span>
            <span className={"font-bold"}>{availableCredit}</span>
          </div>
        </Highlight>
      )}
      {defaultPaymentMethod ? (
        <>
          <DefaultBilling
            defaultMethod={defaultPaymentMethod}
            onClickUpdate={() => setEdit(true)}
          />
        </>
      ) : hasNonGiftSub ? (
        <EditBilling
          inline
          onToken={handleSubmit}
          errorMsg={error}
          buttonLabel={"Update Payment Method"}
        />
      ) : (
        <>
          <Highlight className={"font-bold"}>
            <span className={"text-perriwinkle"}>Add a payment method</span>
          </Highlight>
          <Button
            level={"secondary"}
            className={"float-right mt-4"}
            onClick={() => setEdit(true)}
          >
            Add Payment method
          </Button>
        </>
      )}

      {edit && (
        <BillingModal
          isOpen={true}
          onClose={() => setEdit(false)}
          onToken={handleSubmit}
          errorMsg={error}
          modalTitle={
            defaultPaymentMethod || hasNonGiftSub
              ? "Edit Billing"
              : "Add Billing"
          }
          buttonLabel={defaultPaymentMethod || hasNonGiftSub ? "Update" : "Add"}
        />
      )}
    </Card>
  );
};

const ConnectedBilling = () => {
  const {
    defaultPaymentMethod,
    addPaymentMethod,
    paymentMethodsLoaded,
    outstandingBalance,
    availableCredit,
  } = useContext(AccountContext);
  const { hasNonGiftSub } = useContext(SubscriptionsContext);
  return paymentMethodsLoaded ? (
    <Billing
      defaultPaymentMethod={defaultPaymentMethod}
      addPaymentMethod={addPaymentMethod}
      hasNonGiftSub={hasNonGiftSub}
      outstandingBalance={outstandingBalance}
      availableCredit={availableCredit}
    />
  ) : null;
};

export default ConnectedBilling;
